import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { faSignInAlt, faLock } from '@fortawesome/free-solid-svg-icons';
import Driver from 'driver.js';
import { AuthService } from '../../../auth/services/auth.service';
import { UpsaleModalService } from '../../services/upsale-modal.service';
import { BreadcrumbsService } from '../../services/breadcrumbs.service';
import { RegionSelection } from '../../models/region-selection.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { AuthorizedResourcesService } from '../../../auth/services/authorized-resources.service';
import { Region } from '../../models/region-model';
import { CitiesService } from '../../services/cities.service';
import { IncomeGroup } from '../../models/income-group.model';

// TODO:
// revisit the export data button movement solution
@Component({
  selector: 'mpro-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent implements AfterViewInit, OnInit {
  faSignInAlt = faSignInAlt;
  faLock = faLock;
  @ViewChild('topBar', { read: ElementRef })
  public topBar: ElementRef;

  @ViewChild('stickyHeader', { read: ElementRef })
  public stickyHeader: ElementRef;

  @ViewChild('container', { read: ElementRef })
  public container: ElementRef;

  @ViewChild('exportDataBtn', { read: ElementRef })
  public exportDataBtn: ElementRef;
  public isCategoriesEnabled: boolean;
  public exportDataModal: boolean = false;
  public yearSliderError: string | undefined;

  private headerHeight: number;
  private topBarHeight: number;
  private screenSize: string;
  private exportDataBtnHeight: number;
  private transitionTimeout: any;
  private driver: Driver = new Driver({
    onReset: () => {
      window.scrollTo(0, 0);
      localStorage.setItem('isOnboarded', 'true');
    },
  });
  public isFreemium: boolean = true;

  breadcrumbs$ = this.breadcrumbsService.breadcrumbs$;

  @HostListener('window:resize') // for window scroll events
  onResize() {
    this.getHeights();
    this.getScreenSize();
    this.screenSizeChanges();
    this.setBtnPostion();
  }

  @HostListener('window:scroll') // for window scroll events
  onScroll() {
    if (this.screenSize.trim() !== 'md' && !this.driver.isActivated) {
      // any less than lg
      this.checkIfFixed();
    }
  }
  dataFilters: DataFilters;

  constructor(
    public authService: AuthService,
    private authResources: AuthorizedResourcesService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private upsaleModal: UpsaleModalService,
    private dataFiltersService: DataFiltersService,
    private breadcrumbsService: BreadcrumbsService,
    private citiesService: CitiesService
  ) {
    this.dataFiltersService.getDataFilters().subscribe((filters) => {
      this.dataFilters = filters;
    });
  }

  ngOnInit(): void {
    this.authService.permissions$.subscribe((plan) => {
      this.isFreemium = plan.isFreemium;
    });

    this.authService.categoriesEnabled$.subscribe((isCategoriesEnabled) => {
      this.isCategoriesEnabled = isCategoriesEnabled;
    });
  }

  public openExportData() {
    if (!this.authService.isLoggedIn()) {
      this.upsaleModal.open();
      return;
    }

    this.exportDataModal = true;
  }

  public isIncomeGroupSelected() {
    return this.dataFilters.spendingGroup instanceof IncomeGroup;
  }

  public ngAfterViewInit() {
    this.getHeights();
    this.getScreenSize();
    this.screenSizeChanges();
    this.renderer.setStyle(
      this.container.nativeElement,
      'marginTop',
      `calc(1rem)`
    );
    this.setBtnPostion();
    if (!localStorage.getItem('isOnboarded')) {
      // Define the steps for introduction
      this.driver.defineSteps([
        {
          element: '#first-step',
          popover: {
            className: 'first-step-popover-class',
            title: 'Header Filters',
            description:
              'This is the command center of the World Data Pro application! Filter the data you want to see in the other sections.',
            position: 'bottom-center',
          },
        },
        {
          element: '#second-step',
          popover: {
            title: 'Map',
            description:
              'The heart of the World Data Pro. Here you can explore the data in a beautifully colored interactive map.',
            position: 'left',
          },
        },
        {
          element: '#third-step',
          popover: {
            title: 'Consumer Spending',
            description:
              'Information about the consumer spending of the selected group.',
            position: 'right',
          },
        },
        {
          element: '#fourth-step',
          popover: {
            title: 'Age and Gender Composition',
            description:
              'Information about the age and gender composition of the selected group.',
            position: 'right',
          },
        },
        {
          element: '#fifth-step',
          popover: {
            title: 'Spending Distribution',
            description:
              'Information about the spending distribution of the selected group.',
            position: 'right',
          },
        },
        {
          element: '#sixth-step',
          popover: {
            title: 'Region Comparison',
            description:
              'Here you can visually compare different regions by various metrics.',
            position: 'left',
          },
        },
      ]);

      setTimeout(() => {
        this.driver.start();
      }, 1000); // Start the introduction
    }
  }

  public getScreenSize() {
    this.screenSize = window
      .getComputedStyle(this.elementRef.nativeElement)
      .getPropertyValue('--screen-size');
  }

  public getHeights() {
    this.headerHeight =
      this.stickyHeader.nativeElement.getBoundingClientRect().height;
    this.topBarHeight =
      this.topBar.nativeElement.getBoundingClientRect().height;
    this.exportDataBtnHeight =
      this.exportDataBtn.nativeElement.getBoundingClientRect().height;
  }

  public checkIfFixed(): void {
    if (!this.container.nativeElement || !this.stickyHeader.nativeElement) {
      return;
    }

    this.setBtnPostion();
    if (window.scrollY > this.topBarHeight) {
      this.renderer.setStyle(
        this.container.nativeElement,
        'paddingTop',
        `${this.headerHeight}px`
      );
      this.renderer.addClass(this.stickyHeader.nativeElement, 'sticky');
      this.renderer.addClass(this.exportDataBtn.nativeElement, 'transition');
      this.renderer.addClass(this.exportDataBtn.nativeElement, 'bottom');
    } else {
      this.reset();
      this.resetExportBtn();
    }
  }

  breadcrumbSelected(e: { key: string; value: RegionSelection | Region }) {
    if (!e) {
      this.dataFiltersService.resetAll();
    }

    const currentSelection = this.dataFilters.regionSelection;
    currentSelection.clearAll();
    switch (e.key) {
      case 'continent':
        this.citiesService.getCities$.next(undefined);
        currentSelection.toggleRegion(
          e.value as Region,
          this.authResources.countriesMap,
          false
        );
        break;
      case 'country':
        currentSelection.toggleCountry(e.value as Region);
        break;
    }

    this.dataFiltersService.patchDataFilters({
      regionSelection: this.dataFilters.regionSelection,
    });
  }

  public setError(error?: string) {
    this.yearSliderError = error;
    setTimeout(() => this.getHeights());
  }

  private screenSizeChanges() {
    if (this.screenSize.trim() === 'md') {
      this.reset();
      this.renderer.addClass(this.exportDataBtn.nativeElement, 'transition');
      this.renderer.addClass(this.exportDataBtn.nativeElement, 'bottom');
    } else {
      this.resetExportBtn();
    }
  }

  private setBtnPostion() {
    this.renderer.setStyle(
      this.exportDataBtn.nativeElement,
      'bottom',
      `calc(${
        window.innerHeight -
        this.headerHeight -
        this.topBarHeight -
        this.exportDataBtnHeight +
        window.scrollY
      }px - 1rem)`
    );
  }

  private resetExportBtn() {
    this.renderer.removeClass(this.exportDataBtn.nativeElement, 'bottom');
    clearTimeout(this.transitionTimeout);
    this.transitionTimeout = setTimeout(() => {
      this.renderer.removeClass(
        this.exportDataBtn?.nativeElement,
        'transition'
      );
    }, 250);
  }

  private reset(): void {
    this.renderer.removeStyle(this.container.nativeElement, 'paddingTop');
    this.renderer.removeClass(this.stickyHeader.nativeElement, 'sticky');
  }
}
